import axios, { AxiosResponse } from 'axios'
import { FilterDTO } from 'medere-ui-framework'
import { axiosInstance } from '../App'
import { LoadConfigDTO, SortingFieldDTO } from '../dto/LoadConfigDTO'
import {
    NewBornBreakdownDTO,
    NewBornCriteriaDTO,
    NewBornExportFormat,
    NewBornFilterDTO,
    NewBornGranularity,
    NewBornOptionsDTO,
    NewBornReportRequestDTO,
    NewBornRowDTO,
    NewBornSummaryDTO,
    NewBornTimelinePointDTO,
} from '../dto/newborn/NewBornDTO'
import { parseApiError } from './apiError'

const BASE = '/api/secured/newborn-births'

/** Código de error del backend cuando el perfil no tiene la pantalla en su menú (403). */
export const NEWBORN_ACCESS_DENIED = 'ACCESS_DENIED'

/** True si el backend respondió que el perfil no tiene acceso a la pantalla (403 `ACCESS_DENIED`). */
export function isAccessDenied(error: unknown): boolean {
    const apiError = parseApiError(error)
    return apiError.status === 403 && apiError.errorCode === NEWBORN_ACCESS_DENIED
}

/** True si el pedido se canceló con el `AbortController`: no es un error para mostrar. */
export function isRequestCanceled(error: unknown): boolean {
    return axios.isCancel(error)
}

/**
 * Lo que `RemoteTable` le pasa a `getItems`: sus filtros de columna y su orden, más los filtros de la pantalla que la
 * pantalla le da en `params` (no son de columna: viajan aparte, en `filters`).
 */
export interface NewBornTableParams {
    filters?: NewBornFilterDTO
    filterDTO?: FilterDTO | null
    multiSortMeta?: { fieldName: string; ascOrder: boolean }[]
}

/** Paginación, orden y filtros de la grilla en el formato del backend (`LoadConfigDTO`). Sin página para el Excel. */
function buildLoadConfig(offSet: number | null, pageSize: number | null, params?: NewBornTableParams): LoadConfigDTO {
    const sorting: SortingFieldDTO[] = (params?.multiSortMeta ?? []).map((sort) => ({
        fieldName: sort.fieldName,
        ascOrder: sort.ascOrder,
        sqlFormula: null,
        compositeSortOrder: null,
    }))
    const filterDTO = params?.filterDTO
    const filters = filterDTO && filterDTO.filters.length > 0
        ? {
            associationType: filterDTO.associationType,
            filters: filterDTO.filters.map((filter) => ({
                fieldName: filter.fieldName,
                filterType: filter.filterType,
                sqlType: filter.sqlType ?? null,
                values: filter.values,
                sql: null,
            })),
            filterLists: [],
        }
        : null
    return { offSet, pageSize, sortingFieldInfoList: sorting, filters }
}

/** El cuerpo común: sin filtros de pantalla el backend rechaza el pedido (`MISSING_DATES`), así que acá son obligatorios. */
function criteria(filters: NewBornFilterDTO | undefined, loadConfigDTO: LoadConfigDTO): NewBornCriteriaDTO {
    if (!filters) throw new Error('Faltan los filtros de la pantalla')
    return { filters, loadConfigDTO }
}

/**
 * Con `responseType: 'blob'` el cuerpo de un error también llega como Blob: se lo vuelve JSON para que
 * `parseApiError` lea el `errorCode` y el mensaje del backend.
 */
async function withReadableError<T>(request: Promise<T>): Promise<T> {
    try {
        return await request
    } catch (error: unknown) {
        if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
            try {
                error.response.data = JSON.parse(await error.response.data.text()) as unknown
            } catch {
                // Sin cuerpo JSON: parseApiError cae al mensaje genérico
            }
        }
        throw error
    }
}

/** Una página del listado (scroll infinito de la grilla), con los filtros de la pantalla y los de columna. */
export function getNewBornItems(offSet: number, pageSize: number, params: NewBornTableParams): Promise<NewBornRowDTO[]> {
    const body = criteria(params.filters, buildLoadConfig(offSet, pageSize, params))
    return axiosInstance.post<NewBornRowDTO[]>(`${BASE}/list`, body).then((response) => response.data)
}

/** Indicadores del período y del anterior: se pide una vez por cambio de filtros de la pantalla (no de columna). */
export function getNewBornSummary(filters: NewBornFilterDTO, signal?: AbortSignal): Promise<NewBornSummaryDTO> {
    const body = criteria(filters, buildLoadConfig(null, null))
    return axiosInstance.post<NewBornSummaryDTO>(`${BASE}/summary`, body, { signal }).then((response) => response.data)
}

/**
 * Un punto por semana o mes del rango, con ceros donde no hubo nacimientos. Sin `granularity` el backend elige: semanas hasta
 * 120 días, meses con más.
 */
export function getNewBornTimeline(
    filters: NewBornFilterDTO,
    granularity?: NewBornGranularity | null,
    signal?: AbortSignal,
): Promise<NewBornTimelinePointDTO[]> {
    const body: NewBornCriteriaDTO = { ...criteria(filters, buildLoadConfig(null, null)), ...(granularity ? { granularity } : {}) }
    return axiosInstance.post<NewBornTimelinePointDTO[]>(`${BASE}/timeline`, body, { signal }).then((response) => response.data)
}

/** Repartos de los nacidos vivos del rango (tipo de parto, sexo, destino, edad gestacional y peso). */
export function getNewBornBreakdown(filters: NewBornFilterDTO, signal?: AbortSignal): Promise<NewBornBreakdownDTO> {
    const body = criteria(filters, buildLoadConfig(null, null))
    return axiosInstance.post<NewBornBreakdownDTO>(`${BASE}/breakdown`, body, { signal }).then((response) => response.data)
}

/** Opciones de las listas de los filtros. */
export function getNewBornOptions(signal?: AbortSignal): Promise<NewBornOptionsDTO> {
    return axiosInstance.post<NewBornOptionsDTO>(`${BASE}/options`, undefined, { signal }).then((response) => response.data)
}

/** Hoja de enfermería de la madre de un bebé del listado; null si no hay datos (204). */
export function getNewBornReportPdf(request: NewBornReportRequestDTO): Promise<Blob | null> {
    return withReadableError(axiosInstance
        .post<Blob>(`${BASE}/report`, request, { responseType: 'blob' })
        .then((response) => (response.status === 204 ? null : response.data)))
}

/**
 * Listado filtrado con el resumen, en Excel o PDF: los filtros de la pantalla más los de columna, el buscador y el orden de la
 * grilla (`params` es lo último que pidió la tabla). Devuelve la respuesta completa: `ExportMenu` baja el archivo con el
 * nombre del `Content-Disposition`. Sin `filtersText`, el backend escribe los filtros aplicados en el encabezado.
 */
export function exportNewBornReport(
    format: NewBornExportFormat,
    params: NewBornTableParams,
    filtersText?: string,
): Promise<AxiosResponse<Blob>> {
    const body = { ...criteria(params.filters, buildLoadConfig(null, null, params)), filtersText, format }
    return withReadableError(axiosInstance.post<Blob>(`${BASE}/export`, body, { responseType: 'blob' }))
}
